import {Spec} from 'immutability-helper';
import React, {UpdateSpec} from 'react';

import {NLOToServerId} from 'notebook/ELNConstants';
import NoteLinkedEmbeddingComponent, {ItemProps} from 'notebook/embeddings/NoteLinkedEmbeddingComponent';
import {
  cidPrefix,
  DEFAULT_NOTE_LINKED_EMBEDDING_VIRTUALIZED_HEIGHT,
  EmbeddingProvenance,
  fullSelection,
} from 'notebook/embeddings/NoteLinkedEmbeddingUtil';
import plugins from 'notebook/mediocre/base-plugin/plugins';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps, NoteEditorPlugin} from 'notebook/notes/NoteEditorTypes';
import TEXT_PLUGIN from 'notebook/notes/plugins/text-block/TextPlugin';
import {addCid} from 'notebook/store/StoreTransformUtil';
import {FromServerNoteLinkedObject, NoteLinkedObject, NotePart} from 'notebook/util/NoteUtilConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface NoteLinkedEmbeddingPluginOptions<TNotePart extends NotePart> {
  type: string;
  title: string;
  icon: BenchlingIconName;
  getContentId: (notePart: TNotePart) => string;
  getEmbedding: (notePart: TNotePart) => React.ReactNode;
  getName: (notePart: TNotePart) => string;
  getNameUpdate: (notePart: TNotePart, name: string) => object;
  getProvenance?: (notePart: TNotePart) => EmbeddingProvenance | undefined;
}

// Plugin methods are always called using Function.prototype.call,
// so `this` refers to the editor component in various methods below.
export default function noteLinkedEmbeddingPlugin<TNotePart extends NotePart>(
  options: NoteLinkedEmbeddingPluginOptions<TNotePart>
): NoteEditorPlugin {
  return {
    type: options.type,
    title: options.title,
    icon: options.icon,
    linkable: false,
    alignable: false,
    canPasteFromOtherTenant: false,
    component: plugins.captionWrapper({
      required: [],
      selectionInit: fullSelection,
      getItemProps(this: NoteEditor, item: TNotePart, index: number): ItemProps {
        return {isSelected: this.state.selected.index === index && this.state.selected.subIndex === 'item'};
      },
      make(
        this: NoteEditor,
        item: TNotePart,
        index: number,
        editorProps: NoteEditorProps,
        itemProps: ItemProps,
        searchResultProps: MediocreSearchResultProps
      ): JSX.Element {
        return (
          <NoteLinkedEmbeddingComponent
            editor={this}
            notePart={item}
            index={index}
            editorProps={editorProps}
            itemProps={itemProps}
            searchResultProps={searchResultProps}
            contentId={options.getContentId(item)}
            name={options.getName(item)}
            getNameUpdate={(newName: string) => options.getNameUpdate(item, newName)}
            provenance={options.getProvenance?.(item)}
          >
            {options.getEmbedding(item)}
          </NoteLinkedEmbeddingComponent>
        );
      },
      getVirtualizedHeight: (item: TNotePart, isCollapsed: boolean) =>
        DEFAULT_NOTE_LINKED_EMBEDDING_VIRTUALIZED_HEIGHT,
    }),
    itemToText(this: NoteEditor, item: TNotePart): string {
      return options.getName(item);
    },
    itemToHTML(this: NoteEditor, $el: JQuery, item: TNotePart): JQuery {
      return $el;
    },
    itemFromHTML(this: NoteEditor, $el: JQuery, item: TNotePart): TNotePart | null {
      return item;
    },
    getContent({text, formats}: TNotePart): Partial<TNotePart> {
      return {text, formats} as Partial<TNotePart>;
    },
    setContent({text, formats}: TNotePart): Spec<TNotePart> {
      return {$merge: {text, formats}} as Spec<TNotePart>;
    },
    *getSearchResults(item: NotePart, matcher: TextMatcher): Generator<MediocreSearchResult> {
      // Search over the preview's caption (`item.text`)
      yield* TEXT_PLUGIN.getSearchResults!(item, matcher);
    },
    getText({text, formats}: {text: string; formats: Format}): {text: string; formats: Format} {
      return {text, formats};
    },
    setText({text, formats}: {text: string; formats: Format}): UpdateSpec {
      return {$merge: {text, formats}};
    },
    prepareForSave(item: NoteLinkedObject, cidIdMap: CidIdMap): NLOToServerId {
      return {...item, id: cidIdMap[item.cid] as string};
    },
    prepareForStore(item: FromServerNoteLinkedObject, cidIdMap: CidIdMap): void {
      addCid(item, cidPrefix, cidIdMap);
    },
  };
}
